/**
 * Catalogue unique des couches risques publiées GeoServer (WMS),
 * aligné sur la carte principale (`OLMap`).
 */

export type RiskWmsLayerDef = {
  /** Valeur du paramètre WMS `LAYERS` */
  name: string;
  minZoom?: number;
  maxZoom?: number;
  /** Couches SQL merged_global / fn_*_final : injecte minx..maxy par tuile (VIEWPARAMS). */
  injectTileBboxViewParams?: boolean;
};

/** Clés utilisées par `OLMap` pour `selectedRisk` */
export type RiskLayerUiKey =
  | "fire"
  | "drought"
  | "flood"
  | "pluvialFlood"
  | "submersion"
  | "coastalErosion"
  | "wind"
  | "heavyRain"
  | "hailStorm";

export const RISK_WMS_LAYERS: Record<RiskLayerUiKey, RiskWmsLayerDef | null> = {
  fire: { name: "data4home:fire_map_fixed_with_city" },
  drought: { name: "data4home:drought_high_res_map" },
  flood: {
    name: "data4home:flood_merged_global",
    injectTileBboxViewParams: true,
  },
  pluvialFlood: null,
  submersion: {
    name: "data4home:submersion_merged_global",
    injectTileBboxViewParams: true,
  },
  coastalErosion: {
    name: "data4home:coastal_erosion_merged_global",
    injectTileBboxViewParams: true,
  },
  wind: { name: "data4home:wind_map" },
  heavyRain: { name: "data4home:heavy-rain_map" },
  hailStorm: { name: "data4home:hail_map" },
};

/** Clé API override (`dataset`) → clé UI carte (`selectedRisk` / catalogue) */
export const OVERRIDE_DATASET_TO_UI_KEY: Record<string, RiskLayerUiKey> = {
  coastal_erosion: "coastalErosion",
  submersion: "submersion",
  flood: "flood",
};

/**
 * Couches GeoServer distinctes pour l’éditeur d’override : données de référence vs zones persistées en override.
 * La couche `overrides` doit publier la table PostGIS `*_override` (ex. `risk.coastal_erosion_override`).
 */
export type OverrideDatasetGeoLayers = {
  base: string;
  /**
   * Zoom minimal (View OL) pour la couche référentiel MVT (`base`) dans l’éditeur d’override.
   * Overrides / finale : pas de minZoom par défaut (souvent moins denses).
   */
  baseMinZoom?: number;
  /**
   * Zoom maximal pour l’aperçu WMS image du référentiel (strictement en dessous du MVT si = baseMinZoom).
   * Défaut côté éditeur : même valeur que `baseMinZoom` lorsqu’elle est définie.
   */
  basePreviewMaxZoom?: number;
  /** Couche MVT des overrides persistés ; absent si le dataset n’a pas encore d’édition override. */
  overrides?: string;
  /** Couche publiée sur la vue SQL « carte finale » (composition référentiel + overrides). Optionnel tant que GeoServer n’est pas configuré. */
  final?: string;
};

export const OVERRIDE_DATASET_GEO_LAYERS: Partial<
  Record<string, OverrideDatasetGeoLayers>
> = {
  coastal_erosion: {
    base: "data4home:v_coastal_erosion",
    baseMinZoom: 15,
    overrides: "data4home:coastal_erosion_override",
    final: "data4home:coastal_erosion_merged_global",
  },
  submersion: {
    base: "data4home:v_submersion",
    baseMinZoom: 8,
    overrides: "data4home:marine_submersion_override",
    final: "data4home:submersion_merged_global",
  },
  flood: {
    base: "data4home:v_flood",
    baseMinZoom: 15,
    overrides: "data4home:flood_risk_area_override",
    final: "data4home:flood_merged_global",
  },
};

export function getOverrideDatasetGeoLayers(
  datasetKey: string,
): OverrideDatasetGeoLayers | null {
  const key = datasetKey === "flood_runoff" ? "flood" : datasetKey;
  const entry = OVERRIDE_DATASET_GEO_LAYERS[key];
  return entry ?? null;
}

export function getRiskWmsLayerDef(
  uiKey: string
): RiskWmsLayerDef | null {
  if (!(uiKey in RISK_WMS_LAYERS)) {
    return null;
  }
  return RISK_WMS_LAYERS[uiKey as RiskLayerUiKey];
}

/** Couche WMS « base » pour un dataset d’override (alias du champ `base`). */
export function getPublishedLayerNameForOverrideDataset(
  datasetKey: string,
): string | null {
  return getOverrideDatasetGeoLayers(datasetKey)?.base ?? null;
}
